import NodeViewConstructorArgs from '../constructor/NodeViewConstructorArgs';
import AbstractNodeView from './AbstractNodeView';
import * as d3 from 'd3';
import WorkFlowView from '../view/WorkFlowView';
import View from '../view/View';
import { Syntax } from '@trlogic/ui-pack';
import { EscalatedReason } from '../../../../domain/collection/rule/FlowDomains';
import { NodeType } from '../../../../domain/collection/rule/FlowDomains';
import '../tree.css';
import Icon from '../../../../icons/Icon';
import WorkflowLayoutUtil from '../../../../util/WorkflowLayoutUtil';

enum EscalatedStatus {
  ESCALATED = 'escalated',
  APPROVED = 'approved',
  DECLINED = 'declined',
  DEFAULT = 'default',
}

class EscalatedNodeView extends AbstractNodeView {
  private mainNode: d3.Selection<any, any, any, any> | undefined;
  private menuOpen: boolean = false;

  public constructor(args: NodeViewConstructorArgs) {
    super(args);
  }

  public initialize(): void {
    this.render();
  }

  public render(): void {
    const model = this.getModel<any>();
    if (!model.value.status) {
      model.value.status = EscalatedStatus.DEFAULT;
      model.value.color = '#828282';
    }

    let node: any;
    if (this.shadow) {
      node = d3.select((this.workFlowView as WorkFlowView).getParent()).append('svg');
    } else {
      node = d3
        .select((this.workFlowView as WorkFlowView).getNode())
        .select('#workflow-container-group')
        .append('g');
    }

    this.mainNode = node
      .attr('id', this.el.substring(1, this.el.length))
      .attr('type', 'Escalated')
      .style('position', 'absolute')
      .style('pointer-events', 'auto')
      .style('width', '50px')
      .style('height', '50px')
      .style('cursor', 'pointer')
      .on('click', () => {
        if (!this.menuOpen) {
          const socketRect = this.getSocketBoundingRect();
          this.createDropdownMenu(socketRect);
        }
      });

    this.mainNode
      ?.append('rect')
      .attr('width', 50)
      .attr('height', 50)
      .attr('rx', 3)
      .style('stroke', this.getColor())
      .attr('stroke-width', 1)
      .style('fill', '#fff')
      .style('stroke-dasharray', '0');

    const iconContainer = this.mainNode
      ?.append('foreignObject')
      .attr('width', 28)
      .attr('height', 28)
      .attr('x', 50 / 2 - 14)
      .attr('y', 50 / 2 - 14);

    if (iconContainer) {
      const iconDiv = iconContainer
        .append('xhtml:div')
        .style('width', '100%')
        .style('height', '100%')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center');

      const iconElement = document.createElement('div');
      iconElement.style.width = '100%';
      iconElement.style.height = '100%';
      iconElement.style.display = 'flex';
      iconElement.style.alignItems = 'center';
      iconElement.style.justifyContent = 'center';

      const React = require('react');
      const ReactDOM = require('react-dom');
      ReactDOM.render(
        React.createElement(Icon, {
          icon: this.getIconName(),
          size: 28,
          color: this.getIconColor(),
        }),
        iconElement,
      );

      const iconDivNode = iconDiv.node() as HTMLElement;
      if (iconDivNode && iconDivNode.appendChild) {
        iconDivNode.appendChild(iconElement);
      }
    }

    this.mainNode
      ?.append('text')
      .attr('x', 50 / 2 + 40)
      .attr('y', 50 / 2 - 2)
      .attr('dy', '.35em')
      .style('fill', this.getTextColor())
      .text(this.getText());

    const status = this.getModel<any>().value.status;
    if (status === false) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', '#bdbdbd').style('stroke-dasharray', '6');
      d3.selectAll(this.getNode()).select('text').style('fill', '#bdbdbd');
      d3.selectAll(this.getNode()).select('foreignObject').style('opacity', '0.5');
    }
  }

  public socketHighlight(type?: NodeType) {
    if (!this.isShadow() && type == NodeType.NOT_FRAUD) {
      d3.select(this.getNode()).select('rect').style('stroke', '#2F9BFF').style('stroke-dasharray', '6');
    }
  }

  public socketUnHighlight(type?: NodeType): void {
    if (!this.isShadow() && type == NodeType.NOT_FRAUD) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', this.getColor()).style('stroke-dasharray', '0');
    }
  }

  private getSocketBoundingRect() {
    const socketElement: any = d3.select(this.getNode()).select('rect').node();
    return socketElement.getBoundingClientRect();
  }

  private createDropdownMenu(socketRect: any) {
    if (this.menuOpen) return;
    this.menuOpen = true;
    this.workFlowView.setMenuOpen(true);

    const statuses = [
      { value: EscalatedStatus.ESCALATED, label: 'Escalated', icon: 'arrow_circle_up' },
      { value: EscalatedStatus.APPROVED, label: 'Approved', icon: 'check_circle' },
      { value: EscalatedStatus.DECLINED, label: 'Declined', icon: 'cancel' },
      { value: EscalatedStatus.DEFAULT, label: 'Default', icon: 'datanotfound' },
    ];

    const menuHeight = statuses.length * 40;
    const left = socketRect.right + 20;
    const top = WorkflowLayoutUtil.calculateDropdownTop(socketRect.top, menuHeight);

    const menuContainer = d3
      .select('body')
      .append('ul')
      .attr('id', 'escalated-menu')
      .attr('class', 'escalated-menu')
      .style('position', 'absolute')
      .style('left', left + 'px')
      .style('top', top + 'px')
      .style('background-color', 'white')
      .style('width', '200px')
      .style('border', '1px solid #E0E4E8')
      .style('border-radius', '8px')
      .style('overflow-y', 'auto')
      .style('overflow-x', 'hidden')
      .style('z-index', '1000')
      .style('box-shadow', '0px 12px 16px -4px #00000014');

    statuses.forEach((status) => {
      const listItem = menuContainer
        .append('li')
        .attr('data-status', status.value)
        .style('display', 'flex')
        .style('font-size', '14px')
        .style('height', '40px')
        .style('padding', '10px')
        .style('align-items', 'center')
        .style('cursor', 'pointer');

      const iconContainer = listItem
        .append('div')
        .style('width', '14px')
        .style('height', '14px')
        .style('margin-right', '10px')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center');

      const iconElement = document.createElement('div');
      iconElement.style.width = '14px';
      iconElement.style.height = '14px';
      iconElement.style.display = 'flex';
      iconElement.style.alignItems = 'center';
      iconElement.style.justifyContent = 'center';

      const React = require('react');
      const ReactDOM = require('react-dom');
      ReactDOM.render(
        React.createElement(Icon, {
          icon: status.icon,
          size: 14,
          color: '#4F4F4F',
        }),
        iconElement,
      );

      const iconContainerNode = iconContainer.node() as HTMLElement;
      if (iconContainerNode && iconContainerNode.appendChild) {
        iconContainerNode.appendChild(iconElement);
      }

      listItem.append('span').style('color', '#4F4F4F').style('font-size', '11px').text(status.label);

      listItem
        .on('mouseover', () => {
          listItem.style('background-color', '#F9FAFB');
        })
        .on('mouseout', () => {
          listItem.style('background-color', 'transparent');
        })
        .on('click', () => {
          this.updateStatus(status.value);
          this.closeDropdownMenu();
        });
    });

    d3.select(document).on('pointerup', this.handleOutsideClick);
  }

  private closeDropdownMenu() {
    d3.select('#escalated-menu').remove();
    d3.select(document).on('pointerup', null);
    this.menuOpen = false;
    this.workFlowView.setMenuOpen(false);
    this.workFlowView.reRender();
  }

  private handleOutsideClick = (event: any) => {
    const menuContainer: any = d3.select('#escalated-menu');
    if (this.menuOpen && menuContainer && !menuContainer.node().contains(event.target)) {
      this.closeDropdownMenu();
    }
  };

  private updateStatus(status: EscalatedStatus) {
    const model = this.getModel<any>();
    model.value.status = status;

    let escalatedReason: EscalatedReason;
    switch (status) {
      case EscalatedStatus.ESCALATED:
        escalatedReason = EscalatedReason.ESCALATED;
        model.value.color = '#F09D45';
        model.value.iconColor = '#F09D45';
        break;
      case EscalatedStatus.APPROVED:
        escalatedReason = EscalatedReason.APPROVED;
        model.value.color = '#027A48';
        model.value.iconColor = '#027A48';
        break;
      case EscalatedStatus.DECLINED:
        escalatedReason = EscalatedReason.DECLINED;
        model.value.color = '#D2332F';
        model.value.iconColor = '#D2332F';
        break;
      case EscalatedStatus.DEFAULT:
        escalatedReason = EscalatedReason.DEFAULT;
        model.value.color = '#828282';
        model.value.iconColor = '#828282';
        break;
      default:
        escalatedReason = EscalatedReason.DEFAULT;
        model.value.color = '#828282';
        model.value.iconColor = '#828282';
        break;
    }

    model.value.reason = escalatedReason;

    this.workFlowView.reRender();
  }

  private getCurrentStatus(): EscalatedStatus {
    const model = this.getModel<any>();
    return model.value.status || EscalatedStatus.DEFAULT;
  }

  private getColor(): string {
    const model = this.getModel<any>();
    return model.value.color || '#828282';
  }

  private getIconColor(): string {
    const model = this.getModel<any>();
    return model.value.iconColor || '#828282';
  }

  private getTextColor(): string {
    return '#4F4F4F';
  }

  private getText(): string {
    const status = this.getCurrentStatus();
    switch (status) {
      case EscalatedStatus.ESCALATED:
        return 'Escalated';
      case EscalatedStatus.APPROVED:
        return 'Approved';
      case EscalatedStatus.DECLINED:
        return 'Declined';
      case EscalatedStatus.DEFAULT:
        return 'Default';
      default:
        return 'Default';
    }
  }

  private getIconName(): string {
    const status = this.getCurrentStatus();
    switch (status) {
      case EscalatedStatus.DECLINED:
        return 'circle_inactive';
      case EscalatedStatus.APPROVED:
        return 'circle_active';
      case EscalatedStatus.DEFAULT:
        return 'circle_false';
      case EscalatedStatus.ESCALATED:
        return 'circle_up';
      default:
        return 'circle_false';
    }
  }

  protected preSwap(target: View<Syntax>): boolean {
    switch (target.constructor) {
      case EscalatedNodeView:
        return true;
      default:
        return false;
    }
  }

  public setModel(model: Syntax) {}
}

export default EscalatedNodeView;
